/**
 * The client's photograph as an object URL, fetched once per feed.
 *
 * ONCE, NOT PER ROW. A feed draws the client's mark on every row they acted
 * on, and a download per row would be a request per row for the same bytes.
 * The feed resolves it here and hands the URL down.
 *
 * FETCHED, NOT LINKED, for the reason `HolderMark` gives: the download route
 * is authenticated and scoped to an org and a client, so the bytes arrive as
 * a blob and the object URL is revoked when it changes or the feed goes.
 * `HolderMark` itself is not reused here because its smallest size is 72px
 * and it needs the wizard's lookup provider; this is its fetch, alone.
 *
 * A DANGLING ID IS NO PHOTOGRAPH. An archived document or a failed download
 * resolves to null and the mark draws initials, which is what it draws for
 * everyone else.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { downloadUploadedDocument } from '@biasafe.queries/account-opening-clients-client-id-uploaded-documents-document-id-file/account-opening-clients-client-id-uploaded-documents-document-id-file';

export function useClientPhoto(
  clientId: string,
  documentId?: string | null,
): string | null {
  const { data } = useQuery({
    queryKey: ['client-photo', clientId, documentId ?? ''],
    queryFn: () =>
      // `responseType: 'blob'`, and it has to be said: the generated client
      // types this route's body as `void` because the endpoint streams a file
      // and declares no schema, so without it axios parses the bytes as text.
      downloadUploadedDocument(clientId, documentId ?? '', {
        responseType: 'blob',
      }) as unknown as Promise<Blob>,
    enabled: Boolean(clientId && documentId),
    staleTime: Infinity,
    retry: false,
  });

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!(data instanceof Blob) || data.size === 0) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [data]);

  return url;
}
